import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

/**
 * DURAN VARLIKLAR VE AMORTİSMAN — panel istemcisi.
 *
 * ⚠ CETVEL SUNUCUDA HESAPLANIR. Panelde hiçbir oran, gün sayısı ya da yıl
 * hesabı yoktur: ikinci bir kopya, "ilk yıl tam yıl mı" gibi statüye bağlı bir
 * kuralın iki yerde ayrışmasına ve ekranda görünen rakamın beyandakinden
 * farklı olmasına yeter.
 *
 * ⚠ TUTARLAR METİNDİR; panel aritmetik yapmaz (`money.ts` doktrini).
 */

export const ASSET_KINDS = [
  'BUILDING',
  'INDUSTRIAL_BUILDING',
  'FURNITURE_MACHINE',
  'VEHICLE',
  'IT_EQUIPMENT',
  'SOFTWARE',
  'SMALL_EQUIPMENT',
  'OTHER',
] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];

export interface AssetKindRule {
  key: AssetKind;
  label: string;
  suggestedRatePct: string;
  verified: false;
  note: string;
  vatRevisionYears: 5 | 15;
}

export interface FixedAsset {
  id: string;
  source: string;
  purchaseInvoiceId: string | null;
  purchaseInvoiceLineId: string | null;
  description: string;
  assetKind: AssetKind;
  assetKindLabel: string;
  acquisitionDate: string;
  acquisitionCostExclVat: string;
  capitalisedNonDeductibleVat: string;
  capitalisedCost: string;
  residualValue: string;
  depreciationMethod: string;
  annualRatePct: string;
  firstYearBasis: 'FULL_YEAR' | 'PRO_RATA';
  businessUsePct: string;
  vatRevisionYears: number;
  disposalDate: string | null;
  disposalProceedsExclVat: string | null;
  notes: string | null;

  year: number;
  yearDepreciation: string;
  yearDeductible: string;
  accumulated: string;
  bookValue: string;
  schedule: Array<{
    fiscalYear: number;
    amount: string;
    deductible: string;
    accumulated: string;
    bookValue: string;
    proRataDays: number | null;
  }>;
  warnings: string[];
}

export interface AssetCandidate {
  purchaseInvoiceId: string;
  purchaseInvoiceLineId: string;
  supplierName: string;
  supplierDocumentNumber: string;
  invoiceDate: string;
  description: string;
  baseExclVat: string;
  capitalisedCost: string;
  incomeTaxDeductiblePct: string;
}

export const assetsKey = (year: number) => ['assets', year] as const;
export const assetCandidatesKey = (year: number) => ['assets', 'candidates', year] as const;

export function useAssets(year: number) {
  return useQuery({
    queryKey: assetsKey(year),
    queryFn: () =>
      api.get<{
        items: FixedAsset[];
        totals: {
          capitalisedCost: string;
          yearDepreciation: string;
          yearDeductible: string;
          bookValue: string;
        };
      }>(`/assets?year=${String(year)}`),
  });
}

export function useAssetKinds() {
  return useQuery({
    queryKey: ['assets', 'kinds'],
    queryFn: () => api.get<{ items: AssetKindRule[] }>('/assets/kinds'),
    staleTime: 60 * 60 * 1000,
  });
}

/** Deftere alınmamış yatırım satırları — "bunu deftere al" listesi. */
export function useAssetCandidates(year: number) {
  return useQuery({
    queryKey: assetCandidatesKey(year),
    queryFn: () => api.get<AssetCandidate[]>(`/assets/candidates?year=${String(year)}`),
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['assets'] });
  // ⚠ Amortisman değişince kenara ayrılacak tutar da değişir.
  void qc.invalidateQueries({ queryKey: ['reports', 'tax-reserve'] });
}

export function useCreateAsset(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => api.post<FixedAsset>(`/assets?year=${String(year)}`, body),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateAsset(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) =>
      api.patch<FixedAsset>(`/assets/${id}?year=${String(year)}`, body),
    onSuccess: () => invalidate(qc),
  });
}

export function useDisposeAsset(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) =>
      api.post<FixedAsset>(`/assets/${id}/dispose?year=${String(year)}`, body),
    onSuccess: () => invalidate(qc),
  });
}

export function useUndisposeAsset(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<FixedAsset>(`/assets/${id}/undispose?year=${String(year)}`),
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/assets/${id}`),
    onSuccess: () => invalidate(qc),
  });
}

/**
 * YILIN AMORTİSMAN FİŞİ — durum, önizleme, atma.
 *
 * ⚠ ÖNİZLEME AYRI BİR İSTEKTİR ve bilerek: yasal deftere yazan bir işlemden
 * önce ne yazılacağının GÖRÜLEBİLİR olması gerekir.
 */
export interface DepreciationStatus {
  fiscalYear: number;
  postable: boolean;
  yearEnded: boolean;
  postedEntryId: string | null;
  postedEntryNumber: string | null;
  postedAmount: string | null;
  scheduleAmount: string;
  scheduleDeductible: string;
  assetCount: number;
  /** Fiş ile cetvelin ayrışması — fiş atıldıktan sonra oran değiştiyse doğar. */
  drift: string | null;
}

export interface DepreciationPreview {
  journalCode: string;
  entryDate: string;
  description: string;
  lines: Array<{ accountCode: string; debit: string; credit: string; description: string }>;
  totalCents: number;
  deductibleCents: number;
  nonDeductibleCents: number;
}

export function useDepreciationStatus(year: number) {
  return useQuery({
    queryKey: ['assets', 'depreciation', year],
    queryFn: () => api.get<DepreciationStatus>(`/assets/depreciation/${String(year)}/status`),
  });
}

export function useDepreciationPreview(year: number, enabled: boolean) {
  return useQuery({
    queryKey: ['assets', 'depreciation', year, 'preview'],
    enabled,
    retry: false,
    queryFn: () => api.get<DepreciationPreview>(`/assets/depreciation/${String(year)}/preview`),
  });
}

export function usePostDepreciation(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<unknown>(`/assets/depreciation/${String(year)}`),
    onSuccess: () => {
      invalidate(qc);
      // ⚠ Fiş defteri de değiştirir: mizan ve yevmiye listesi tazelenmeli.
      void qc.invalidateQueries({ queryKey: ['accounting'] });
    },
  });
}

/**
 * Uyarı metinleri — her biri NE OLDUĞUNU ve NE YAPILACAĞINI söyler.
 */
export const ASSET_WARNINGS: Record<string, string> = {
  RATE_NOT_STATUTORY:
    'Amortisman oranı KANUNDAN gelmiyor: Belçika’da varlık türü başına yasal süre yoktur, idare varlığın GERÇEK kullanım ömrüne uygun bir süre ister. Buradaki oran yaygın idari toleranstır ve değiştirilebilir.',
  BELOW_VAT_CAPITAL_GOODS_THRESHOLD:
    'Maliyeti 1.000 €’nun altında: KDV açısından duran varlık SAYILMAZ (KB nr. 3 md. 6 §2) ve revizyon cetveline girmez. Gelir vergisinde amortise edilmesine engel değildir.',
  DISPOSAL_CONSEQUENCES_NOT_MODELLED:
    'Elden çıkarıldı. ⚠ İki sonucu bu yazılım HESAPLAMIYOR: revizyon dönemi içindeyse KDV revizyonu (KB nr. 3 md. 10, kutu 61/62) ve değer artış kazancının vergilendirilmesi (WIB92 md. 171, 4°). İkisini de kendiniz değerlendirin.',
  FULLY_DEPRECIATED:
    'Amortismanı bitmiş: seçilen yılda indirilecek tutar yok. Varlık hâlâ kullanımdaysa defterde kalması doğrudur.',
};
